import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Package } from 'lucide-react';
import './productCard.css';

/* ProductCard — one result in a browse grid (`.sf-prod`).
   The React port of SP-main's `SF.prodCard`. Photo, brand, name, price — see
   productCard.css for what was deliberately taken off it.

   Two links, not one clickable div: the photo and the name open the product, the
   brand line opens that brand. A wrapping <div onClick> is not keyboard
   reachable, announces nothing and cannot be opened in a new tab. `href` makes
   these real links; `onClick` is for a router that wants to intercept, and still
   receives the event so it can `preventDefault`.

   The image fallback is the caller's: `onImageError` exists because this app
   re-syncs a product's images when one 404s, and a shared component does not
   know about that endpoint. */
export default function ProductCard({
  name,
  brand,
  price,
  image,
  href,
  onClick,
  brandHref,
  onBrandClick,
  objectFit,
  onImageError,
  footer,
}) {
  const [broken, setBroken] = useState(false);

  const handleImageError = () => {
    setBroken(true);
    if (onImageError) onImageError();
  };

  /* A link that has somewhere to go is an <a>; one that only has a handler is a
     <button>, so it stays in the tab order and is announced as an action. */
  const Tag = href ? 'a' : 'button';
  const linkProps = href ? { href } : { type: 'button' };

  return (
    <div className="sf-prod">
      <Tag
        className={`sf-pimg${objectFit === 'cover' ? ' sf-pimg-cover' : ''}`}
        aria-label={name}
        onClick={onClick}
        {...linkProps}
      >
        {image && !broken ? (
          <img src={image} alt={name} loading="lazy" onError={handleImageError} />
        ) : (
          <span className="ph"><Package aria-hidden="true" /></span>
        )}
      </Tag>

      <div className="sf-pb">
        {brand && (
          brandHref || onBrandClick ? (
            <a
              className="sf-pbrand"
              href={brandHref}
              title={brand}
              onClick={(e) => {
                /* The brand link sits inside a card whose photo and name go
                   somewhere else. Without this, clicking it fires both. */
                e.stopPropagation();
                if (onBrandClick) onBrandClick(e);
              }}
            >
              {brand}
            </a>
          ) : (
            <span className="sf-pbrand" title={brand}>{brand}</span>
          )
        )}

        <Tag className="sf-pname" onClick={onClick} {...linkProps}>{name}</Tag>

        <div className="sf-pfoot">
          <span className="sf-price">{price}</span>
          {footer}
        </div>
      </div>
    </div>
  );
}

ProductCard.propTypes = {
  name: PropTypes.string.isRequired,
  brand: PropTypes.string,
  /** Already formatted — "$50.00", or a range "$10.00 - $100.00". */
  price: PropTypes.node,
  image: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  brandHref: PropTypes.string,
  onBrandClick: PropTypes.func,
  /** `cover` fills the frame (the reference). `contain` for packshots. */
  objectFit: PropTypes.oneOf(['cover', 'contain']),
  /** The image 404'd. The placeholder is already drawn by the time this runs. */
  onImageError: PropTypes.func,
  /** Anything that belongs beside the price. Empty on the storefront. */
  footer: PropTypes.node,
};

ProductCard.defaultProps = { objectFit: 'contain' };
